import React, { useState, useEffect } from "react";
import "./Hierarchy.css";

function Hierarchy({
  elements,
  setElements,
  selectedElement,
  setSelectedElement,
  getRootElements,
}) {
  const [draggedElement, setDraggedElement] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [isValidDrop, setIsValidDrop] = useState(true);
  const [expandedElements, setExpandedElements] = useState(new Set());
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    elementId: null
  });
  const [clipboard, setClipboard] = useState(null);

  // Move the moveElement function here
  const moveElement = (draggedId, targetId, position = "inside") => {
    if (draggedId === targetId) return;

    setElements(prevElements => {
      const draggedElement = prevElements.find(el => el.id === draggedId);
      if (!draggedElement) return prevElements;

      const oldParentElement = prevElements.find(el => el.children?.includes(draggedId));
      const targetElement = targetId ? prevElements.find(el => el.id === targetId) : null;
      const targetParentElement = targetElement?.parentId ? 
        prevElements.find(el => el.children?.includes(targetId)) : null;

      let newElements = [...prevElements];

      switch (position) {
        case 'before':
        case 'after': {
          if (!targetElement) return newElements;

          // Handle reordering within the same parent
          if (oldParentElement && targetParentElement && oldParentElement.id === targetParentElement.id) {
            return newElements.map(el => {
              if (el.id === oldParentElement.id) {
                const newChildren = [...el.children];
                const draggedIndex = newChildren.indexOf(draggedId);
                const targetIndex = newChildren.indexOf(targetId);
                
                // Remove dragged element
                newChildren.splice(draggedIndex, 1);
                
                // Insert at new position
                const insertIndex = position === 'before' ? 
                  targetIndex : 
                  targetIndex + (draggedIndex < targetIndex ? 0 : 1);
                
                newChildren.splice(insertIndex, 0, draggedId);

                return {
                  ...el,
                  children: newChildren
                };
              }
              return el;
            });
          }

          // Handle root level moves
          if (!targetParentElement) {
            // First, remove from old parent
            if (oldParentElement) {
              newElements = newElements.map(el => {
                if (el.id === oldParentElement.id) {
                  return {
                    ...el,
                    children: el.children.filter(id => id !== draggedId)
                  };
                }
                return el;
              });
            }

            const rootElements = newElements.filter(el => !el.parentId);
            const updatedDraggedElement = { ...draggedElement, parentId: null };
            let newRootElements = rootElements.filter(el => el.id !== draggedId);
            const targetIndex = newRootElements.findIndex(el => el.id === targetId);

            if (targetIndex > -1) {
              newRootElements.splice(
                position === 'before' ? targetIndex : targetIndex + 1,
                0,
                updatedDraggedElement
              );
            }

            const nonRootElements = newElements.filter(el => el.parentId !== null && el.id !== draggedId);
            return [...newRootElements, ...nonRootElements];
          }

          // Handle moves between different parents
          // First remove from old parent
          if (oldParentElement) {
            newElements = newElements.map(el => {
              if (el.id === oldParentElement.id) {
                return {
                  ...el,
                  children: el.children.filter(id => id !== draggedId)
                };
              }
              return el;
            });
          }

          // Then add to new parent's children at the correct position
          return newElements.map(el => {
            if (el.id === targetParentElement.id) {
              const newChildren = [...el.children];
              const targetIndex = newChildren.indexOf(targetId);
              newChildren.splice(
                position === 'before' ? targetIndex : targetIndex + 1,
                0,
                draggedId
              );
              return {
                ...el,
                children: newChildren
              };
            }
            if (el.id === draggedId) {
              return {
                ...el,
                parentId: targetParentElement.id
              };
            }
            return el;
          });
        }
          
        case 'inside': {
          // Prevent dropping inside own parent or child
          const isChildOfDragged = (parentId) => {
            if (!parentId) return false;
            const parent = newElements.find(el => el.id === parentId);
            if (!parent) return false;
            if (parent.id === draggedId) return true;
            return isChildOfDragged(parent.parentId);
          };

          if (targetId === oldParentElement?.id || isChildOfDragged(targetId)) {
            return prevElements;
          }

          if (oldParentElement) {
            newElements = newElements.map(el => {
              if (el.id === oldParentElement.id) {
                return {
                  ...el,
                  children: el.children.filter(id => id !== draggedId)
                };
              }
              return el;
            });
          }

          return newElements.map(el => {
            if (el.id === targetId) {
              return {
                ...el,
                children: [...(el.children || []), draggedId]
              };
            }
            if (el.id === draggedId) {
              return {
                ...el,
                parentId: targetId
              };
            }
            return el;
          });
        }

        default:
          return newElements;
      }
    });
  };

  // Add delete function
  const deleteElement = (elementId) => {
    setElements(prevElements => {
      // First, get all descendant IDs to remove
      const getDescendantIds = (id) => {
        const element = prevElements.find(el => el.id === id);
        if (!element || !element.children) return [];
        return [
          ...element.children,
          ...element.children.flatMap(childId => getDescendantIds(childId))
        ];
      };

      const descendantIds = getDescendantIds(elementId);
      const idsToRemove = [elementId, ...descendantIds];

      // Remove element from its parent's children array
      const updatedElements = prevElements.map(el => {
        if (el.children?.includes(elementId)) {
          return {
            ...el,
            children: el.children.filter(id => id !== elementId)
          };
        }
        return el;
      });

      // Remove the element and all its descendants
      return updatedElements.filter(el => !idsToRemove.includes(el.id));
    });

    // Clear selection if deleted element was selected
    if (selectedElement?.id === elementId) {
      setSelectedElement(null);
    }
  };

  // Add duplicate function
  const duplicateElement = (elementId) => {
    const generateNewId = () => Date.now() + Math.floor(Math.random() * 1000);
    
    // Helper function to deep clone an element and its children
    const cloneElement = (sourceId) => {
      const source = elements.find(el => el.id === sourceId);
      if (!source) return null;

      const newId = generateNewId();
      const builderId = `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Clone children recursively
      const clonedChildren = (source.children || []).map(childId => {
        return cloneElement(childId);
      }).filter(Boolean);

      // Create new element with new ID and builderId
      const newElement = {
        ...source,
        id: newId,
        builderId: builderId,
        children: clonedChildren.map(child => child.element.id)
      };

      return {
        element: newElement,
        children: clonedChildren
      };
    };

    // Clone the element and all its descendants
    const cloned = cloneElement(elementId);
    if (!cloned) return;

    setElements(prevElements => {
      const sourceElement = prevElements.find(el => el.id === elementId);
      
      // Flatten all new elements (including nested children)
      const getAllElements = (clone) => {
        const elements = [clone.element];
        clone.children.forEach(child => {
          elements.push(...getAllElements(child));
        });
        return elements;
      };

      const allNewElements = getAllElements(cloned);
      
      // If source element has a parent, add the clone to parent's children
      if (sourceElement.parentId) {
        return prevElements.map(el => {
          if (el.id === sourceElement.parentId) {
            // Find the index of the source element in parent's children
            const sourceIndex = el.children.indexOf(elementId);
            const newChildren = [...el.children];
            // Insert the cloned element after the source element
            newChildren.splice(sourceIndex + 1, 0, cloned.element.id);
            
            return {
              ...el,
              children: newChildren
            };
          }
          return el;
        }).concat(allNewElements);
      }
      
      // If it's a root element, just add it to the elements array
      return [...prevElements, ...allNewElements];
    });

    // Select the newly created element
    setSelectedElement(cloned.element);
  };

  // Update keyboard event listener to handle both delete and duplicate
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedElement) {
        if (e.key === 'Delete') {
          deleteElement(selectedElement.id);
        } else if (e.key === 'd' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault(); // Prevent browser's default behavior
          duplicateElement(selectedElement.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement]); // Add dependencies

  // Helper function to check if drop is valid
  const isValidDropTarget = (draggedId, targetId) => {
    if (draggedId === targetId) return false;
    
    const draggedElement = elements.find(el => el.id === draggedId);
    const targetElement = elements.find(el => el.id === targetId);
    
    // Prevent div from being dropped into button
    if (draggedElement?.type === 'div' && targetElement?.type === 'button') {
      return false;
    }
    
    // Only prevent dropping INSIDE current parent
    if (draggedElement.parentId === targetId && dropPosition === 'inside') return false;
    
    // Can't drop on own child
    let currentParent = targetElement;
    while (currentParent) {
      if (currentParent.id === draggedId) return false;
      currentParent = elements.find(el => el.id === currentParent.parentId);
    }
    
    return true;
  };

  // Helper function to determine drop position
  const getDropPosition = (e, elementRect) => {
    const mouseY = e.clientY;
    const elementY = elementRect.top;
    const elementHeight = elementRect.height;
    
    const topThreshold = elementY + elementHeight * 0.25;
    const bottomThreshold = elementY + elementHeight * 0.75;

    if (mouseY < topThreshold) return 'before';
    if (mouseY > bottomThreshold) return 'after';
    return 'inside';
  };

  const getElementTypeIcon = (type) => {
    switch (type) {
      case 'div':
      case 'section':
        return { icon: '□', class: 'container' };
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
        return { icon: 'H', class: 'content' };
      case 'p':
      case 'text':
        return { icon: 'T', class: 'content' };
      case 'image':
        return { icon: '▣', class: 'media' };
      case 'video':
        return { icon: '▶', class: 'media' };
      case 'button':
        return { icon: '⬥', class: 'content' };
      case 'list':
        return { icon: '≡', class: 'content' };
      case 'link':
        return { icon: '⇲', class: 'content' };
      case 'input':
        return { icon: '⌸', class: 'content' };
      case 'form':
        return { icon: '⊟', class: 'container' };
      default:
        return { icon: '◆', class: 'container' };
    }
  };

  // Add this helper function at the top of your component
  const isChildOfSelected = (element, selectedElement, elements) => {
    if (!selectedElement || !element) return false;
    
    let currentElement = element;
    while (currentElement.parentId) {
      if (currentElement.parentId === selectedElement.id) {
        return true;
      }
      currentElement = elements.find(el => el.id === currentElement.parentId);
    }
    return false;
  };

  // Add toggle function
  const toggleExpand = (elementId, e) => {
    e.stopPropagation();
    setElements(prevElements => {
      return prevElements.map(el => {
        if (el.id === elementId) {
          return {
            ...el,
            isExpanded: !el.isExpanded
          };
        }
        return el;
      });
    });
  };

  // Add cut function
  const cutElement = (elementId) => {
    const element = elements.find(el => el.id === elementId);
    setClipboard({ type: 'cut', element });
    deleteElement(elementId);
  };

  // Add copy function
  const copyElement = (elementId) => {
    const element = elements.find(el => el.id === elementId);
    setClipboard({ type: 'copy', element });
  };

  // Add paste function
  const pasteElement = (targetId) => {
    if (!clipboard?.element) return;
    
    if (clipboard.type === 'cut') {
      // Move the element
      moveElement(clipboard.element.id, targetId, 'after');
      setClipboard(null);
    } else if (clipboard.type === 'copy') {
      // Duplicate the element
      duplicateElement(clipboard.element.id);
      // Don't clear clipboard to allow multiple pastes
    }
  };

  // Add select parent function
  const selectParent = (elementId) => {
    const element = elements.find(el => el.id === elementId);
    if (element?.parentId) {
      const parent = elements.find(el => el.id === element.parentId);
      handleElementSelect(parent);
    }
  };

  // Handle context menu click
  const handleContextMenu = (e, elementId) => {
    e.preventDefault();
    e.stopPropagation();
    
    const element = elements.find(el => el.id === elementId);
    handleElementSelect(element);
    
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      elementId
    });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(prev => ({ ...prev, show: false }));
    };

    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  // Helper function to render hierarchy items recursively
  const renderHierarchyItems = (elementIds, level = 0) => {
    return elementIds.map((id) => {
      const element = elements.find((el) => el.id === id);
      if (!element) return null;

      const parentElement = elements.find(el => el.id === element.parentId);
      const isDirectListItem = element.type === "listItem" && parentElement?.type === "list";
      const firstClassName = element.className?.split(' ')[0];
      const { icon, class: iconClass } = getElementTypeIcon(element.type);
      const hasChildren = element.children && element.children.length > 0;
      const isExpanded = element.isExpanded;

      let displayText;
      if (isDirectListItem) {
        displayText = "List Item";
      } else if (firstClassName) {
        displayText = firstClassName;
      } else if (element.hierarchyName) {
        displayText = element.hierarchyName;
      } else {
        displayText = element.text || element.type;
      }

      return (
        <div key={element.id} style={{ marginLeft: `${level * 8}px` }}>
          <div className={`hierarchy-item-wrapper ${
            dropTarget === element.id && dropPosition === 'before' ? 'drop-before' : ''
          } ${
            dropTarget === element.id && dropPosition === 'after' ? 'drop-after' : ''
          }`}>
            <div
              className={`hierarchy-item ${
                selectedElement?.id === element.id ? "selected" : ""
              } ${
                isChildOfSelected(element, selectedElement, elements) ? "selected-parent" : ""
              } ${
                dropTarget === element.id && dropPosition === 'inside' ? 
                  (isValidDrop ? "drop-target" : "invalid-drop-target") 
                  : ""
              }`}
              draggable="true"
              onClick={(e) => {
                e.stopPropagation();
                handleElementSelect(element);
                setContextMenu(prev => ({ ...prev, show: false }));
              }}
              onContextMenu={(e) => handleContextMenu(e, element.id)}
              onDragStart={(e) => {
                setDraggedElement(element.id);
                e.dataTransfer.setData("text/plain", element.id);
                e.currentTarget.classList.add("dragging");
              }}
              onDragEnd={(e) => {
                e.currentTarget.classList.remove("dragging");
                setDraggedElement(null);
                setDropTarget(null);
                setDropPosition(null);
                setIsValidDrop(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const draggedId = parseInt(draggedElement);
                const isValid = isValidDropTarget(draggedId, element.id);
                setIsValidDrop(isValid);
                setDropTarget(element.id);
                
                const rect = e.currentTarget.getBoundingClientRect();
                const position = getDropPosition(e, rect);
                setDropPosition(position);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (dropTarget === element.id) {
                  setDropTarget(null);
                  setDropPosition(null);
                  setIsValidDrop(true);
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const draggedId = parseInt(e.dataTransfer.getData("text/plain"));
                if (isValidDropTarget(draggedId, element.id)) {
                  moveElement(draggedId, element.id, dropPosition);
                }
                setDropTarget(null);
                setDropPosition(null);
                setIsValidDrop(true);
              }}
            >
              {hasChildren && (
                <span 
                  className={`expand-collapse-icon ${isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => toggleExpand(element.id, e)}
                >
                  ▸
                </span>
              )}
              <span className={`element-icon ${iconClass}`}>{icon}</span>
              <span className="element-name">{displayText}</span>
              <span className="element-type-badge">{element.type}</span>
            </div>
          </div>
          {hasChildren && isExpanded && (
            <div className="children-container">
              {renderHierarchyItems(element.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Update the handleRootDrop function
  const handleRootDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Process if we're dropping on hierarchy-view, hierarchy-list, or outside valid drop targets
    const isValidTarget = e.target.classList.contains('hierarchy-view') || 
                         e.target.classList.contains('hierarchy-list');
    
    // Move element to root level if dropped on hierarchy area or outside valid drop targets
    const draggedId = parseInt(e.dataTransfer.getData("text/plain"));
    if (draggedId) {
      setElements(prevElements => {
        return prevElements.map(el => {
          if (el.id === draggedId) {
            // Remove from parent
            return {
              ...el,
              parentId: null
            };
          }
          // Remove from old parent's children array
          if (el.children?.includes(draggedId)) {
            return {
              ...el,
              children: el.children.filter(id => id !== draggedId)
            };
          }
          return el;
        });
      });
    }
    
    setDropTarget(null);
    setDropPosition(null);
    setIsValidDrop(true);
  };

  // Add handleRootContextMenu function
  const handleRootContextMenu = (e) => {
    if (e.target.classList.contains('hierarchy-view') || 
        e.target.classList.contains('hierarchy-list')) {
      e.preventDefault();
      e.stopPropagation();
      
      handleElementSelect(null);
      setContextMenu({
        show: true,
        x: e.clientX,
        y: e.clientY,
        elementId: null
      });
    }
  };

  // Add this helper function to get all parent IDs
  const getAllParentIds = (elementId) => {
    const parentIds = [];
    let currentElement = elements.find(el => el.id === elementId);
    
    while (currentElement?.parentId) {
      parentIds.push(currentElement.parentId);
      currentElement = elements.find(el => el.id === currentElement.parentId);
    }
    
    return parentIds;
  };

  // Add useEffect to watch for selectedElement changes from Preview
  useEffect(() => {
    if (selectedElement) {
      // Get all parent IDs of the selected element
      const parentIds = getAllParentIds(selectedElement.id);
      
      // Expand all parents by setting isExpanded to true
      setElements(prevElements => 
        prevElements.map(el => {
          if (parentIds.includes(el.id)) {
            return {
              ...el,
              isExpanded: true
            };
          }
          return el;
        })
      );
    }
  }, [selectedElement]); // Watch for changes in selectedElement

  // Keep the existing handleElementSelect function
  const handleElementSelect = (element) => {
    if (element) {
      // Get all parent IDs of the selected element
      const parentIds = getAllParentIds(element.id);
      
      // Expand all parents by setting isExpanded to true
      setElements(prevElements => 
        prevElements.map(el => {
          if (parentIds.includes(el.id)) {
            return {
              ...el,
              isExpanded: true
            };
          }
          return el;
        })
      );
    }
    
    setSelectedElement(element);
  };

  return (
    <div 
      className="hierarchy-view"
      onClick={(e) => {
        if (e.target.classList.contains('hierarchy-view') || 
            e.target.classList.contains('hierarchy-list')) {
          setSelectedElement(null);
        }
      }}
      onContextMenu={handleRootContextMenu}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTarget("root");
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const isValidTarget = e.target.classList.contains('hierarchy-view') || 
                            e.target.classList.contains('hierarchy-list');
        if (isValidTarget) {
          setDropTarget(null);
        }
      }}
      onDrop={handleRootDrop}
    >
      <div 
        className={`hierarchy-list ${dropTarget === "root" ? "root-drop-target" : ""}`}
        onContextMenu={handleRootContextMenu}
      >
        {renderHierarchyItems(getRootElements().map((el) => el.id))}
      </div>

      {/* Updated Context Menu */}
      {contextMenu.show && (
        <div 
          className="context-menu"
          style={{ 
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          <div 
            className={`context-menu-item ${!contextMenu.elementId ? 'disabled' : ''}`}
            onClick={() => {
              if (contextMenu.elementId) {
                cutElement(contextMenu.elementId);
                setContextMenu(prev => ({ ...prev, show: false }));
              }
            }}
          >
            Cut
          </div>
          <div 
            className={`context-menu-item ${!contextMenu.elementId ? 'disabled' : ''}`}
            onClick={() => {
              if (contextMenu.elementId) {
                copyElement(contextMenu.elementId);
                setContextMenu(prev => ({ ...prev, show: false }));
              }
            }}
          >
            Copy
          </div>
          {clipboard && (
            <div className="context-menu-item" onClick={() => {
              pasteElement(contextMenu.elementId);
              setContextMenu(prev => ({ ...prev, show: false }));
            }}>
              Paste
            </div>
          )}
          <div 
            className={`context-menu-item ${!contextMenu.elementId ? 'disabled' : ''}`}
            onClick={() => {
              if (contextMenu.elementId) {
                duplicateElement(contextMenu.elementId);
                setContextMenu(prev => ({ ...prev, show: false }));
              }
            }}
          >
            Duplicate
          </div>
          <div 
            className={`context-menu-item ${!contextMenu.elementId ? 'disabled' : ''}`}
            onClick={() => {
              if (contextMenu.elementId) {
                deleteElement(contextMenu.elementId);
                setContextMenu(prev => ({ ...prev, show: false }));
              }
            }}
          >
            Delete
          </div>
          <div 
            className={`context-menu-item ${!contextMenu.elementId ? 'disabled' : ''}`}
            onClick={() => {
              if (contextMenu.elementId) {
                selectParent(contextMenu.elementId);
                setContextMenu(prev => ({ ...prev, show: false }));
              }
            }}
          >
            Select Parent
          </div>
        </div>
      )}
    </div>
  );
}

export default Hierarchy;
